import React from "react";
import { ReactComponent as NotStreakIcon } from "../../../../../assects/svg/Insight/Not_streak_icon.svg";
import { ReactComponent as PartialLogStreakIcon } from "../../../../../assects/svg/Insight/Prtial_log_streak_icon.svg";
import { ReactComponent as LogStreakIcon } from "../../../../../assects/svg/Insight/Log_streak_icon.svg";

type DayLogStatus = "none" | "partial" | "logged" | "upcoming";

interface FortnightlyStreakCardProps {
  currentStreak?: number;
  longestStreak?: number;
  daysMissed?: number;
  week1Start?: Date;
  dayLogs?: DayLogStatus[];
}

const GrayCircle = () => (
  <div className="w-7 h-7 3xl:w-8 3xl:h-8 rounded-full bg-[#E5E7EB]" />
);

const FortnightlyStreakCard: React.FC<FortnightlyStreakCardProps> = ({
  currentStreak,
  longestStreak,
  daysMissed,
  week1Start,
  dayLogs,
}) => {
  const isLoading = dayLogs == null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const anchor = (() => {
    const d = week1Start ? new Date(week1Start) : new Date(today);
    d.setHours(0, 0, 0, 0);
    if (!week1Start) d.setDate(d.getDate() - d.getDay());
    return d;
  })();

  const getEffectiveStatus = (idx: number): DayLogStatus => {
    const dayDate = new Date(anchor);
    dayDate.setDate(anchor.getDate() + idx);
    dayDate.setHours(0, 0, 0, 0);

    if (dayDate > today) {
      const status = dayLogs?.[idx];
      return status === "logged" || status === "partial" ? status : "upcoming";
    }
    return dayLogs?.[idx] ?? "none";
  };

  const renderDayIcon = (status: DayLogStatus) => {
    switch (status) {
      case "logged":
        return <LogStreakIcon className="w-7 h-7 3xl:w-8 3xl:h-8" />;
      case "partial":
        return <PartialLogStreakIcon className="w-7 h-7 3xl:w-8 3xl:h-8" />;
      case "none":
        return <NotStreakIcon className="w-7 h-7 3xl:w-8 3xl:h-8" />;
      case "upcoming":
        return <GrayCircle />;
    }
  };

  const statBoxClass = "flex-1 bg-[#F5F6F7] rounded-lg px-4 py-3 flex flex-col gap-1";
  const statLabelClass = "text-[12px] 3xl:text-[13px] text-[#8D8D8D] font-inter";
  const statValueClass = "text-[16px] 3xl:text-[18px] font-semibold text-[#1F1F1F] font-inter";

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter mb-3">
        Streak
      </span>

      <div className="flex gap-3 mb-4">
        <div className={statBoxClass}>
          <span className={statLabelClass}>Current streak</span>
          <span className={statValueClass}>
            {isLoading ? (
              <span className="inline-block w-14 h-5 rounded bg-[#E5E7EB]" />
            ) : (
              `${currentStreak ?? 0} days`
            )}
          </span>
        </div>
        <div className={statBoxClass}>
          <span className={statLabelClass}>Days missed</span>
          <span className={statValueClass}>
            {isLoading ? (
              <span className="inline-block w-8 h-5 rounded bg-[#E5E7EB]" />
            ) : (
              daysMissed ?? 0
            )}
          </span>
        </div>
        <div className={statBoxClass}>
          <span className={statLabelClass}>Longest streak</span>
          <span className={statValueClass}>
            {isLoading ? (
              <span className="inline-block w-14 h-5 rounded bg-[#E5E7EB]" />
            ) : (
              `${longestStreak ?? 0} days`
            )}
          </span>
        </div>
      </div>

      {/* 14-day icon row */}
      <div
        className="grid gap-x-1 mt-4 "
        style={{ gridTemplateColumns: "repeat(14, minmax(0, 1fr))" }}
      >
        {Array.from({ length: 14 }, (_, i) => {
          const d = new Date(anchor);
          d.setDate(anchor.getDate() + i);
          return (
            <div key={i} className="flex flex-col items-center gap-1">
              {isLoading ? <GrayCircle /> : renderDayIcon(getEffectiveStatus(i))}
              <span className="text-[10px] 3xl:text-[11px] text-[#8E8E93] font-inter leading-none">
                {d.getDate()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FortnightlyStreakCard;
